/**
 * AppLayout — Main application shell layout.
 *
 * Sidebar + header + main content area. Handles command palette
 * global keyboard shortcut (Cmd+K / Ctrl+K), the Transaction
 * Wizard (+ New button, Cmd+N shortcut), keyboard sequence
 * shortcuts (N→I, G→D, etc.), and the shortcut help overlay (?).
 *
 * @since 1.0.0
 */
import { useState, useEffect, useCallback, useRef, useMemo, useContext, lazy, Suspense, type ReactNode } from 'react';
import { AppLayoutMountedContext } from './appLayoutMounted';
import { useSideView, workArea, MAX_WINDOWS } from '../sideview/useSideView';
import { isSideView, isSideViewShortcut } from '../sideview/frame';
import { toast } from '@/shared/ui/toast';
// fastphone-4: each from its own file, never the components barrel. The barrel
// re-exports the AI panel, the transaction wizard, the invoice drawer and the
// tools dock, and importing anything through it put all of them in the code the
// first screen waits for.
import { Sidebar } from '../components/Sidebar';
import { getNavZonesForLevel, applyOrgTypeGating, applyModuleGating, getPersonZones } from '../components/sidebarNav';
import { isIndividualOrg } from '../me/join';
import { verticalDoorsOnly } from '../components/verticalDoors';
import { SimpleHubLinks } from '../components/SimpleHubLinks';
import { ModuleGate } from '../components/ModuleGate';
import type { CommandItem, PaletteWhere } from '../components/CommandPalette';
import type { Place } from '../whereis/places';
import type { ShutReason } from '../whereis/canOpen';
import type { HelpWhich } from '../help/HelpPanel';
import { KarrMark } from '../components/KarrLogo';
import { CopyOrgNameButton } from '../components/CopyOrgNameButton';
// office-books — the office altitude's own left rail: one word until opened.
import { OfficeBooksNav } from '../components/OfficeBooksNav';
import { useEntitlements } from '../hooks/useEntitlements';
import { useModules } from '../hooks/useModules';
import { SIMPLE_HUB_NAV_ITEMS } from '../components/SimpleHubLinks';
import { isNavItemVisibleByModule } from '../config/modules';
import { Plus, FileText, Receipt, Search, Settings, Zap, LayoutDashboard, Users, Package, BarChart3, ShoppingCart, Calculator, CalendarDays, Globe, Landmark, Keyboard, ClipboardCheck, Heart, Shield, WifiOff, RefreshCw, CloudOff, LogOut, KeyRound, GraduationCap, Menu, ChevronDown, Briefcase, Volume2, VolumeX, Home, Store, LayoutGrid, CircleHelp, Headset, Lightbulb, BookOpen,
} from 'lucide-react';
import { isUiSoundOn, getUiVolume } from '../utils/soundPrefs';
import { markActiveSoon } from '../utils/workDays';
import { ROLE_LABELS } from '@/shared/lib/capabilities';

// Platform-correct modifier key for shortcut hints (⌘ on Mac, Ctrl+ elsewhere).
const MOD_KEY = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform || navigator.userAgent)
    ? '⌘' : 'Ctrl+';
import { usePlatformAdmin } from '@/shared/hooks/usePlatformAdmin';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useAuth } from '../hooks/useAuth';
import { hasPassword } from '@/shared/auth/passwordPrompt';
import { useNavBadges } from '../hooks/useNavBadges';
import { supabase } from '@/db/supabase';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { NetworkStatusBanner } from '../components/NetworkStatusBanner';
import { DoodleBackground } from '../doodles';
import { PageHeader } from '../components/PageHeader';
import { FitText } from '../components/FitText';
import { PracticeBanner } from '../components/PracticeBanner';
import { useGetStarted } from '../hooks/useGetStarted';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { BillingGate } from '../components/BillingGate';
import { LayoutHeaderContext } from '../contexts/LayoutHeaderContext';
import { getRouteMeta, ROUTE_TERM_KEY } from '../config/routeRegistry';
import { useNotifications, useNotificationEngine } from '../hooks/useNotifications';
import { CrmSweepWorker } from '@/shared/crm/CrmSweepWorker';
import { MonthRunPillSlot } from '@/shared/components/MonthRunPillSlot';
import { useCopilotUpdateCheck, useCopilotUpdateStore } from '@/shared/roc/eform/copilotUpdate';
import { GstDisconnectTicker } from '@/shared/components/GstDisconnectTicker';
import { useLiveRefreshManager } from '../hooks/useLiveRefresh';
import { useExperienceLevel } from '../hooks/useExperienceLevel';
import { getModuleKeyForPath } from '../guide/guidePaths';
import { translateTerm } from '../config/experienceLevel';
import { applyAuditorGating } from '../config/auditorNav';
import { OFFICE_ROUTE, isOfficeRoute, isVisitingClientBooks } from '../config/officeIdentity';
import { showsMyBusinessesLink } from '../config/myBusinesses';
import { useOfficeIdentity } from '../hooks/useOfficeIdentity';
import { ViewSwitch } from '../components/ViewSwitch';
import { ThemeButton, ThemeChoice } from '../components/ThemeSwitch';
import { BranchScopeSwitcher } from '../components/BranchScopeSwitcher';
import { useBranchScope } from '../hooks/useBranchScope';
import { floatingCensus } from './floatingControls';
import { flowHidesSidebar } from './flowSidebar';
import { openSidebar } from './sidebarOpen';
import { leavesVertical, showBooksButton, useVerticalNav, verticalActiveHref, verticalBackLabel, verticalZonesFor } from './verticalNav';
import { useVerticalAccess } from '../access/useVerticalAccess';
import { pageAccess, zonesForAccess } from '../access/verticalAccess';
import { setTraceBack } from '../trace/traceBack';
import { lockedUntil, unlockPanel, usePanelLock } from '@/shared/panelLock/panelLock';
import { LazyPanelLockButton as PanelLockButton } from '@/shared/panelLock/LazyPanelLockButton';
import { firstNameFor } from '../utils/personName';
import { useHandoffState, type HandoffState } from '@/shared/flow/handoff';
import type { WizardPrefill } from '@/shared/flow/prefill';
import { usePushLanding } from '@/shared/push/pushLanding';
import { isPhone } from '@/shared/push/webPush';

// fillrail - lazy: the shell is the main chunk. The one panel that holds
// everything a popup used to ask for (profile details, Get Started, a new law,
// a Copilot update, installing the app, a module's first tour).
const FillRail = lazy(() => import('../fillrail/FillRail'));
// vsidebar-b: drawn only for a page the person may not open; it carries ErrorWithFix and the fix rules, so never in the first load.
const VerticalRefusal = lazy(() => import('../access/VerticalRefusal').then((m) => ({ default: m.VerticalRefusal })));
// panellock: lazy, it draws only after `?`; this paid for the side-panel lock in the first load.
const ShortcutHelp = lazy(() => import('../components/ShortcutHelp').then((m) => ({ default: m.ShortcutHelp })));
// exactpos-bc (22 Oct 2026): lazy, it draws only when the phone's menu is opened; it was 7 KB of the first load.
const MobileOrgPanel = lazy(() => import('../components/MobileOrgPanel').then((m) => ({ default: m.MobileOrgPanel })));
// guidepath: the coach and every module's coaching words load on a coached page,
// never in the first load (it does not open by itself here: autoOpen is false).
const ModuleCoach = lazy(() => import('../guide/ModuleCoach').then((m) => ({ default: m.ModuleCoach })));
// flow-1b: the question flow is lazy too. It was eager, and the main chunk had
// 1.8KB gz of headroom left under its budget. Imported from its own file, not
// the components barrel, so nothing eager pulls it back into the main chunk.
const TransactionWizard = lazy(() =>
    import('../components/TransactionWizard/TransactionWizard').then(m => ({ default: m.TransactionWizard })));
// sidewin: Karr's floating windows (founder, 26 Sep 2026). Lazy: nothing of
// them loads until the first window opens.
const SideWindows = lazy(() => import('../sideview/SideWindows'));
// mainslim: the Copilot update ask is fetched only when there is one to ask
// (it was 11.7 KB of the main chunk, which sat over its 350 KB budget).
const CopilotUpdatePopup = lazy(() => import('@/shared/components/CopilotUpdatePopup').then((m) => ({ default: m.CopilotUpdatePopup })));
// fastphone-4: tools nobody has opened yet load on first use, not with the shell.
const KarrAIPanel = lazy(() => import('../components/KarrAIPanel').then((m) => ({ default: m.KarrAIPanel })));
// orgidchips: P G C T draw only inside the open switcher, so they stay out of the first load.
const loadOrgIdChips = () => import('../components/OrgIdChips');
const OrgIdChips = lazy(() => loadOrgIdChips().then((m) => ({ default: m.OrgIdChips })));
// orgidchips: the business list draws only when the switcher opens, so it left the first load
// too (main sat at exactly the 350 KB budget, and the P G C T letters needed room).
const loadOrgSwitchList = () => import('../components/OrgSwitchList');
const OrgSwitchList = lazy(() => loadOrgSwitchList().then((m) => ({ default: m.OrgSwitchList })));
const ToolsDock = lazy(() => import('../components/ToolsDock').then((m) => ({ default: m.ToolsDock })));
const VoucherDrawer = lazy(() => import('../components/VoucherDrawer').then((m) => ({ default: m.VoucherDrawer })));
const Karrphone = lazy(() => import('../components/Karrphone').then((m) => ({ default: m.Karrphone })));
const MonthRunWorker = lazy(() => import('@/shared/office/MonthRunWorker').then((m) => ({ default: m.MonthRunWorker })));
const ComplianceOrb = lazy(() => import('../components/ComplianceOrb').then((m) => ({ default: m.ComplianceOrb })));
// whereis-6: the Ctrl+K palette is its own chunk, fetched on the first open; it
// mounts only while open, so the first load carries none of it.
const PaletteWithClients = lazy(() => import('../components/PaletteWithClients'));
/** Fetch the palette's chunk as a finger or pointer reaches the search button, so the press does not wait for it. */
const prefetchPalette = () => { void import('../components/PaletteWithClients'); };
// helpsupport: Help and Karr support beside the search box. The buttons are drawn
// here; their lists and the phone's sheet are a chunk fetched on the first press.
const HelpPanel = lazy(() => import('../help/HelpPanel'));
const prefetchHelp = () => { void import('../help/HelpPanel'); };
// improve: Improve Karr, the sheet a person sends an idea from. Its own chunk, on the first press.
const ImproveKarr = lazy(() => import('../improve/ImproveKarr'));
// bulbglow: the speaker's panel draws only on a press, so it is a chunk of its own too.
const SoundPanel = lazy(() => import('./SoundPanel'));
const prefetchSound = () => { void import('./SoundPanel'); };
const prefetchImprove = () => { void import('../improve/ImproveKarr'); };
/** bulbglow: set once this person has opened Improve Karr here; the lightbulb stops glowing. */
const IMPROVE_OPENED_KEY = 'karr_improve_opened';
const TallyMirrorBanner = lazy(() => import('../components/TallyMirrorBanner').then((m) => ({ default: m.TallyMirrorBanner })));
const WindowBridge = lazy(() => import('../sideview/WindowBridge'));
const WindowNav = lazy(() => import('../sideview/WindowNav'));
// webpushask: asked on phones only, so a laptop never fetches it.
const PushAskPopup = lazy(() => import('@/shared/push/PushAskPopup'));
const ON_PHONE = isPhone();

/**
 * sidewin: this Karr is running INSIDE one of the floating windows of another
 * Karr. Decided once, at boot: the iframe's name outlives every navigation in
 * it. In a window the layout draws the sidebar and the page, and none of what
 * the Karr around it already has once (header, bell, workers, fill rail, dock,
 * floating pills), so nothing runs twice and no header sits inside a header.
 */
const IN_WINDOW = isSideView();

export interface AppLayoutProps {
    children: ReactNode;
    /** Current route path for sidebar highlighting (auto-detected if not provided) */
    currentRoute?: string;
    /** Organization name (auto-detected from auth if not provided) */
    orgName?: string;
}

// AppLayoutMountedContext (nested-mount pass-through) lives in appLayoutMounted.ts,
// so the side view's shell can switch the same pass-through on inside its panel.

/**
 * The bell's engine, as a component, for one reason: **it must run exactly once
 * per app, and a hook cannot be called conditionally.**
 *
 * `useNotificationEngine()` used to be called straight from AppLayout's body,
 * above the old `isAlreadyMounted` check (fastphone-1 has since moved that check
 * above every hook, see `AppLayout`), and the consequence was that 101 page components which wrap
 * themselves in `<AppLayout>` (a deliberate pass-through for the sidebar) each
 * started a SECOND engine on top of the layout route's: two sockets on every
 * topic, two polls, two of every fetch, and two independent `seen` sets, so one
 * arrival raised two toasts. The founder saw the same "3 new notifications /
 * Invitation to join TIGER CABLE INDUSTRIES" toast twice, 11 Aug 2026.
 *
 * Conditional *rendering* is legal where a conditional *call* is not, so the
 * engine lives in here and only the outermost AppLayout renders it. Anything
 * that must be single-instance for the whole app belongs in this component, not
 * in AppLayout's body.
 */
function NotificationEngine() {
    useNotificationEngine();
    // webpush: a tap on a phone notification lands here, once for the app.
    usePushLanding();
    useCopilotUpdateCheck();
    // copupgrade: the update ask, once for the whole app like the check above.
    // webpushask: and on a phone, "Get Karr's alerts on this phone" until it is on.
    return (
        <>
            <CopilotUpdateMount />
            {ON_PHONE && <Suspense fallback={null}><PushAskPopup /></Suspense>}
        </>
    );
}

/**
 * mainslim: mounts the update ask the first time there is an update (or the
 * Settings card reopens it), and keeps it mounted after: its Done step stays on
 * screen once the store's update is cleared.
 */
function CopilotUpdateMount() {
    const wanted = useCopilotUpdateStore((s) => !!s.update || s.forceOpen !== null);
    const [mounted, setMounted] = useState(false);
    if (wanted && !mounted) setMounted(true);
    return mounted ? <Suspense fallback={null}><CopilotUpdatePopup /></Suspense> : null;
}

/**
 * Main layout shell with sidebar, command palette, wizard, and content area.
 *
 * fastphone-1: the layout route mounts the shell once (`src/router.tsx`), and
 * about 115 route files ALSO wrap themselves in `<AppLayout>`. That inner copy
 * must be a pure pass-through, and it must return BEFORE any hook: the shell's
 * hooks (get started, nav badges, branch scope, onboarding status) used to run
 * in the inner copy too, 15 server calls on every page change whose answers
 * nothing drew. So this export reads the mounted flag and nothing else; every
 * hook lives in `AppLayoutShell`, which only the outermost copy renders.
 * Conditional rendering is legal where a conditional hook call is not.
 */
export function AppLayout(props: AppLayoutProps) {
    const isAlreadyMounted = useContext(AppLayoutMountedContext);
    if (isAlreadyMounted) return <>{props.children}</>;
    return <AppLayoutShell {...props} />;
}

function AppLayoutShell({ children, currentRoute: explicitRoute, orgName: explicitOrgName }: AppLayoutProps) {
    // NOTE: the bell's engine is NOT called here. It is rendered as
    // <NotificationEngine /> below, only when this is the outermost AppLayout —
    // see that component for why. `useNotifications()` is a pure reader of the
    // shared store and is safe to call from any number of mounts.
    const { notifications: realtimeNotifications, markRead: markNotifRead, markAllRead: markAllNotifsRead } = useNotifications();
    // App-wide live data: refetch open screens on tab focus + org realtime changes.
    useLiveRefreshManager();
    const { orgs, currentOrg, switchOrg, user, signOut } = useAuth();
    const { isOnline, pendingChanges, isSyncing, syncNow } = useOfflineSync();
    const routerState = useRouterState();
    const { level: experienceLevel, initialize: initExperience, setLevel: setExperienceLevel } = useExperienceLevel();
    const { has: hasEntitlement } = useEntitlements();

    const [commandOpen, setCommandOpen] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState<HelpWhich | null>(null);
    const helpRef = useRef<HTMLDivElement>(null);
    const closeHelp = useCallback(() => setHelpOpen(null), []);
    // improve: the Improve Karr sheet, from its header button or the phone's Help sheet.
    const [improveOpen, setImproveOpen] = useState(false);
    // bulbglow: the lightbulb glows until this person first opens Improve Karr on this device.
    const [improveNew, setImproveNew] = useState(() => {
        try { return localStorage.getItem(IMPROVE_OPENED_KEY) !== '1'; } catch { return true; }
    });
    const openImprove = useCallback(() => {
        setHelpOpen(null);
        setImproveOpen(true);
        setImproveNew(false);
        try { localStorage.setItem(IMPROVE_OPENED_KEY, '1'); } catch { /* private window: it glows again next time */ }
    }, []);
    // ...and from a bell row about the person's idea, which lands with ?idea=<id>. It opens
    // once per arrival: closing it remembers the id rather than editing the address.
    const [ideaSeen, setIdeaSeen] = useState<string | null>(null);
    // UI tick sounds (scroll arrows etc.) — preferences persisted in localStorage.
    const [uiSound, setUiSound] = useState(isUiSoundOn);
    const [uiVol, setUiVolState] = useState(getUiVolume);
    const [soundOpen, setSoundOpen] = useState(false);
    // heyname: when the person starts and ends each day, on this device (a timestamp compare per
    // press, a write at most once a minute, no call). The + sheet greets by it.
    useEffect(() => {
        markActiveSoon();
        const on = () => markActiveSoon();
        window.addEventListener('pointerdown', on, { passive: true, capture: true });
        window.addEventListener('keydown', on, { passive: true, capture: true });
        return () => {
            window.removeEventListener('pointerdown', on, { capture: true });
            window.removeEventListener('keydown', on, { capture: true });
        };
    }, []);
    // orgidfast: the switcher's list and chips are a lazy chunk (kept out of the
    // first load), so fetch it once the page has drawn and the browser is idle.
    // The founder found the switcher slower to open once it waited for that
    // chunk on the click; now the click finds it already here.
    useEffect(() => {
        const warm = () => { void loadOrgSwitchList(); void loadOrgIdChips(); };
        if ('requestIdleCallback' in window) {
            const id = window.requestIdleCallback(warm, { timeout: 4000 });
            return () => window.cancelIdleCallback(id);
        }
        const t = setTimeout(warm, 2000);
        return () => clearTimeout(t);
    }, []);
    // Close the sound panel on any click outside it.
    useEffect(() => {
        if (!soundOpen) return;
        const onDown = (e: MouseEvent) => {
            if (!(e.target as HTMLElement)?.closest?.('[data-sound-panel]')) setSoundOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [soundOpen]);
    const [profileOpen, setProfileOpen] = useState(false);
    const [wizardInitialPath, setWizardInitialPath] = useState<string | undefined>();
    const [wizardInitialSubPath, setWizardInitialSubPath] = useState<string | undefined>();
    // flow-2: the Needs you row "Continue" reopens the org's saved draft.
    const [wizardResumeDraft, setWizardResumeDraft] = useState(false);
    // flow-2c: finishing an operation reuses its id, so its posted steps are recognised.
    const [wizardOperationId, setWizardOperationId] = useState<string | undefined>();
    // flow-8: what a bank line or a document already says, for the question flow.
    const [wizardPrefill, setWizardPrefill] = useState<WizardPrefill | undefined>();
    // fixdue: the fixed expense a reminder opened the record step for.
    const [wizardFixedTemplate, setWizardFixedTemplate] = useState<string | undefined>();
    // flow-3: a handoff coming back from the real form (its Done card, or Who again).
    const [wizardHandoff, setWizardHandoff] = useState<HandoffState | null>(null);
    const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
    // The role the current user holds in the active org — 'owner' = your own org,
    // anything else = a client/shared org you were granted access to.
    const currentOrgRole = (currentOrg as { role?: string } | null)?.role;
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    // panellock: the menu's lock, for the phone drawer.
    const menuLock = usePanelLock('menu');
    const profileRef = useRef<HTMLDivElement>(null);
    const orgDropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const isPlatformAdmin = usePlatformAdmin();

    // Auto-detect route from router if not explicitly provided
    const currentRoute = explicitRoute || routerState.location.pathname || '/';

    // improve: a bell row about the person's idea lands with ?idea=<id> and the
    // sheet opens on it. Not on Karr's own inbox, which reads the same address.
    const ideaParam = (() => {
        const v = (routerState.location.search as Record<string, unknown> | undefined)?.idea;
        return typeof v === 'string' && v && !currentRoute.startsWith('/admin') ? v : null;
    })();
    // Each arrival has its own history key, so a second press on the same bell row opens it again.
    const arrival = (() => {
        const st = routerState.location.state as { key?: string; __TSR_key?: string } | undefined;
        return `${ideaParam}@${st?.__TSR_key ?? st?.key ?? routerState.location.href}`;
    })();
    const landedIdea = ideaParam && arrival !== ideaSeen ? ideaParam : null;
    const closeImprove = useCallback(() => { setImproveOpen(false); setIdeaSeen(arrival); }, [arrival]);

    // Onboarding gate — drives the persistent setup banner and locks the
    // compliance orb until the org's essentials are filled.
    const onboarding = useOnboardingStatus(currentOrg?.id);
    // Auto-detect org name from auth if not explicitly provided
    const orgName = explicitOrgName || currentOrg?.name || 'My Organization';

    // Initialize experience level when org changes
    useEffect(() => {
        if (currentOrg?.id) {
            initExperience(currentOrg.id);
            // Check if there's a pending level from onboarding
            try {
                const pending = localStorage.getItem('karr_pending_experience_level');
                if (pending && (pending === 'business_owner' || pending === 'finance_manager' || pending === 'expert')) {
                    setExperienceLevel(pending as 'business_owner' | 'finance_manager' | 'expert');
                    localStorage.removeItem('karr_pending_experience_level');
                }
            } catch { /* ignore */ }
        }
    }, [currentOrg?.id, initExperience, setExperienceLevel]);

    // Get navigation zones adapted to the user's experience level + business nature,
    // then decorate items with live counts (pending requests, retainers due).
    const navBadges = useNavBadges();
    // Practice orgs run a practice: their sidebar is the office, not the books.
    // The ob-5 `is_practice` flag is the authoritative signal; the entitlement
    // stays as a fallback for orgs granted Practice by plan (e.g. comps) — but
    // ONLY on books of your own (office-boundary). A plan that grants
    // `practice_dashboard` says something about a subscription, not about whose
    // firm you are standing in, and on a client's org it repainted their whole
    // sidebar as an office they don't have.
    const isPracticeOrg = Boolean((currentOrg as { is_practice?: boolean } | null)?.is_practice)
        || (hasEntitlement('practice_dashboard') && currentOrgRole === 'owner');
    // wv-2 — the way back to the office must survive standing inside a CLIENT's
    // books (cc-11), so the header pill keys on "do I have a practice at all",
    // never on how many orgs I can see: a business owner's second business is
    // not an office, and a brand-new org must never grow one by accident.
    //
    // office-boundary (founder, 2026-08-10): "only consultants should get the
    // Office option". "Any org I can see is a practice" was still too loose —
    // it fired for anyone merely invited into a firm, and for a client whose
    // plan carried the entitlement. The office is the one you OWN, and that
    // rule now lives with the post-login fork so the two cannot disagree.
    // …and the other half of the boundary: am I standing in somebody else's
    // books on behalf of my firm? (Wider than ownership: a firm's staff sent
    // into a client is exactly as much of a guest as its partner.)
    // Both come from the one hook, so Settings and the module wall answer this
    // the same way the header does (office-guest).
    const { officeOrg, visiting: visitingClientBooks, clientName, phoneOffice } = useOfficeIdentity();
    // brctx-1 — load the reading lens for whichever org is open. Keyed on the
    // org id alone: `load` replaces the whole slice, so switching business never
    // leaves the previous one's branches (or a branch id from them) in place.
    const loadBranchScope = useBranchScope((s) => s.load);
    useEffect(() => { void loadBranchScope(currentOrg?.id); }, [currentOrg?.id, loadBranchScope]);

    // The office door. It must move the ORG, not just the route: the pill used
    // to navigate to /portfolio while leaving the client org active, so a
    // consultant clicking "Office" from inside a client's books landed on that
    // client's Practice Suite and was told the module is turned off for their
    // organization. switchOrg also ends any "viewing client" visit, so the
    // banner and its Exit can't go stale behind us.
    const goToOffice = useCallback(() => {
        if (!officeOrg) return;
        if (currentOrg?.id !== officeOrg.id) switchOrg(officeOrg.id);
        navigate({ to: OFFICE_ROUTE });
    }, [officeOrg, currentOrg?.id, switchOrg, navigate]);
    // cc-10: firm-altitude routes — the header pill lights up, and in a practice
    // org this is also what tells the two altitudes apart (office-one, below).
    // `/my-work` joined the list with office-one: it is a practice employee's
    // home (postLoginLanding O8), so it was always the office rather than a set
    // of books, and it is the one office surface a staffer ever sees.
    const practiceMode = isOfficeRoute(currentRoute);
    const getStarted = useGetStarted(currentOrg?.id);
    // The activation layer — which modules exist for this org (mod-2).
    const { active: activeModules } = useModules();
    // Read through the cast OUT here rather than inside the memo: a cast hides
    // the dependency from the exhaustive-deps rule, which then asks for the whole
    // `currentOrg` and would rebuild the nav on every unrelated org change.
    const orgRole = (currentOrg as { role?: string } | null)?.role;
    // vsidebar-b: which industry desks and whether Books this person opens here (one read a
    // session, none for an owner or admin). Null while unknown hides nothing.
    const vAccess = useVerticalAccess();
    // mybiz: "My Businesses" is the person's own group, not this org's page, so
    // inside a client's books it steps out of the nav (it read as ANATORQ's).
    const myBusinessesLink = showsMyBusinessesLink(currentOrg?.id, orgs);
    // law 2 (docs/VERTICALS_ROADMAP.md §1): on a vertical's pages its own list
    // rides this same sidebar, handed in by the vertical's area (verticalNav.ts).
    const verticalNav = useVerticalNav(s => s.nav);
    const verticalZonesAll = verticalZonesFor(verticalNav, currentRoute);
    // vsidebar-b: a vertical's own Books row goes for a person without Books.
    const verticalZones = useMemo(() => (verticalZonesAll ? zonesForAccess(verticalZonesAll, vAccess) : null), [verticalZonesAll, vAccess]);
    const { navZones: booksZones } = useMemo(() => {
        // mejoin: a person's own year has no books to walk.
        if (isIndividualOrg(currentOrg?.org_type)) return { navZones: getPersonZones() };
        let zones = getNavZonesForLevel(
            experienceLevel,
            currentOrg?.business_nature as import('../config/businessNature').BusinessNature | undefined,
            currentOrg?.org_type,
        );
        zones = applyOrgTypeGating(zones, currentOrg?.org_type);
        // Module gating — a no-op for legacy orgs (everything on), so nav stays
        // pixel-identical until an org explicitly picks its modules (mod-3).
        zones = applyModuleGating(zones, activeModules);
        // aud-3 — an auditor came to examine the books, not to run the business.
        // A no-op for every other role, so nav is untouched for everyone else.
        zones = applyAuditorGating(zones, orgRole);
        // vsidebar-onerow: a vertical is one row here, its door; its desks ride its own sidebar.
        zones = verticalDoorsOnly(zones);
        if (!myBusinessesLink) {
            zones = zones
                .map(z => ({
                    ...z,
                    groups: z.groups
                        .map(g => ({ ...g, items: g.items.filter(it => it.id !== 'my-businesses') }))
                        .filter(g => g.items.length > 0),
                }))
                .filter(z => z.groups.length > 0);
        }
        zones = zones.map(z => ({
            ...z,
            groups: z.groups.map(g => ({
                ...g,
                items: g.items.map(it => (navBadges[it.id] ? { ...it, badge: navBadges[it.id] } : it)),
            })),
        }));
        // fillrail: the Get Started journey is rows in the fill rail now, not a
        // sidebar badge that got ignored. /get-started stays, linked from there.
        // cc-10 (founder-directed): Practice lives in the HEADER now — the
        // constant strip where "my everything is on the top". The sidebar is
        // entity navigation only, so the Practice group leaves it entirely
        // (its four surfaces are the /portfolio page's own top tabs).
        zones = zones
            .map(z => ({ ...z, groups: z.groups.filter(g => g.id !== 'grp-practice') }))
            .filter(z => z.groups.length > 0);
        // office-one (founder, 2026-08-10): a consultant who signed up came for
        // clients, not for eighty rows of a books product — so at the office
        // altitude the books step back out of the way. They just are not a
        // CONTROL any more. wv-2 put a "This firm" toggle at the foot of the
        // sidebar, which read as a second firm-altitude door next to the header's
        // Office pill ("office is this firm, why do we have this firm
        // separately"). The altitude is a fact about WHERE YOU ARE, so it is
        // derived from the route and there is nothing to switch.
        //
        // office-books (2026-08-11) settled how far "out of the way" goes: these
        // same zones now ride the office's rail (OfficeBooksNav), shut by
        // default and one click from the whole nav. Stepping back is not the
        // same as being deleted, and the firm's row in the office roster was
        // never a discoverable door on its own.
        // vsidebar-b: a desk this person may not open leaves the list, and without Books so do the books' rows.
        zones = zonesForAccess(zones, vAccess);
        return { navZones: zones };
    }, [experienceLevel, currentOrg?.business_nature, currentOrg?.org_type, orgRole, activeModules, navBadges, myBusinessesLink, vAccess]);
    const navZones = verticalZones ?? booksZones;
    // vsidebar-a: a vertical's rows keep their tab in the address (`/stay?tab=front`), so the open
    // row is matched on the search too; the books' rows on the path alone, as before.
    const searchStr = routerState.location.searchStr ?? '';
    const activeRoute = verticalZones ? verticalActiveHref(verticalZones, currentRoute, searchStr) : currentRoute;

    // ob-2 — Simple's sidebar is six rows, so the surfaces it no longer carries
    // live inside the screen they belong to (SimpleHubLinks). This is what that
    // strip suppresses: anything already visible as a sidebar row.
    const navItemIds = useMemo(
        () => new Set(navZones.flatMap(z => z.groups.flatMap(g => g.items.map(i => i.id)))),
        [navZones],
    );
    // …and those same screens stay pinnable, so a favourite set before the cut
    // still resolves. Module-gated like everything else.
    const pinnableHubItems = useMemo(
        () => (experienceLevel === 'business_owner'
            ? SIMPLE_HUB_NAV_ITEMS.filter(i => isNavItemVisibleByModule(i.id, activeModules))
            : undefined),
        [experienceLevel, activeModules],
    );

    // Hide sidebar on full-screen pages like the Content Engine
    // (Karr Travel since ts-31 and Karr Health since hf-fix-3 are in this shell, law 2: their lists ride the sidebar.)
    const hideSidebar = currentRoute === '/karr-agents';

    // wv-7 (founder-directed, said again 1 Oct 2026): Flow has NO sidebar, on
    // any set of books, the firm's own included. Only the office pages keep
    // their rail. The rule and its reasons live in flowSidebar.ts.
    // A vertical's pages keep their list in Flow too: it is the only way between its desks.
    const flowNoSidebar = !verticalZones && flowHidesSidebar({ isFlow: experienceLevel === 'business_owner', isPracticeOrg, practiceMode });

    // office-one: the office is ONE page with its own tabs (PracticeTabs), so it
    // does not carry the ordinary entity sidebar. The books nav belonged to an
    // entity all along; at the firm altitude there is no entity, and wv-2's
    // answer to that was a sidebar holding a single row that only switched
    // itself off.
    //
    // office-books (founder, 2026-08-11): but deleting the rail outright left
    // the office as the one screen in Karr with a blank left margin, and made
    // the consultant's OWN books reachable only through a row inside the office
    // roster that you have to know is there. So the office gets a RAIL instead
    // of nothing — closed it is one word, Books; open it is the ordinary books
    // nav for the firm's own org, which is exactly the org you are standing in
    // when this is true. It switches no altitude: the header pill is still the
    // only way up, this is only the way down. See OfficeBooksNav.
    const officeBooksRail = !verticalZones && isPracticeOrg && practiceMode;
    const noSidebar = hideSidebar || flowNoSidebar || officeBooksRail;
    // …but the office DOES have a nav to open on a phone now, so the hamburger
    // and its drawer key on this rather than on `noSidebar`. Flow and the
    // full-screen pages genuinely have nothing to open.
    const sidebarAvailable = !hideSidebar && !flowNoSidebar;

    // switchside (founder, 3 Oct 2026): "the moment a new client is switched to
    // it should auto open the sidebar ... when you choose a new client, what
    // option do you want to go to for him now". A switch to ANOTHER org (never
    // the first org of a session) is remembered until the page in front of you
    // has the books sidebar, then opens it once: expanded on a laptop, the
    // drawer on a phone. The office's own pages wait (the switch usually lands
    // there a beat before the navigation does), and Flow never gets one, which
    // the founder kept when asking for this (Books view only).
    const lastOrgId = useRef<string | null>(currentOrg?.id ?? null);
    const openOnArrival = useRef<string | null>(null);
    useEffect(() => {
        const id = currentOrg?.id ?? null;
        if (id && lastOrgId.current && id !== lastOrgId.current) openOnArrival.current = id;
        lastOrgId.current = id;
        if (!id || openOnArrival.current !== id || noSidebar || IN_WINDOW) return;
        openOnArrival.current = null;
        // panellock: kept shut on purpose for a while; the switch does not open it.
        if (lockedUntil('menu')) return;
        if (window.matchMedia('(min-width: 768px)').matches) openSidebar();
        // eslint-disable-next-line react-hooks/set-state-in-effect -- once per switch, guarded by the ref above
        else setMobileMenuOpen(true);
    }, [currentOrg?.id, noSidebar]);
    // flow-1b (FLOW_SCREEN_SPEC.md §1.3): what may float on a phone, and where.
    // On the Flow home nothing floats; on a deep Flow screen at most the Home
    // pill (Karr Force rides in it) and Record. Books is unchanged.
    const census = floatingCensus({
        isFlow: experienceLevel === 'business_owner',
        currentRoute,
        hideSidebar: !!hideSidebar,
        flowNoSidebar,
    });

    // sidewin: "duplicates the current screen", floating on top, as many
    // times as works (founder, 26 Sep 2026). Wherever there is a sidebar to
    // work from, never inside a window itself (the window hands Ctrl+\ back).
    const sideViewAvailable = sidebarAvailable && !IN_WINDOW;
    const sideWindows = useSideView((s) => s.windows);
    const windowCount = sideWindows.length;
    const restoreWindow = useSideView((s) => s.restore);
    // A chip names its page only where two names fit whole beside Search;
    // squeezed to "Re…" a name says less than the number does.
    const chipNames = sideWindows.filter((w) => w.minimized).length <= 2;
    const orgNameOf = useCallback((id: string) => orgs.find(o => o.id === id)?.name ?? null, [orgs]);
    const hereHref = routerState.location.pathname + (routerState.location.searchStr ?? '');
    const openWindow = useCallback((href?: string, title?: string) => {
        const at = href ?? hereHref;
        const label = title || getRouteMeta(at.split('?')[0])?.title || 'Karr';
        if (!useSideView.getState().open(at, label, workArea())) {
            toast.warning(`${MAX_WINDOWS} windows are open, the most Karr runs at once. Close one to open another.`);
        }
    }, [hereHref]);

    // Close profile dropdown on click outside / Escape
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
                setProfileOpen(false);
            }
        }
        function handleEscape(e: KeyboardEvent) {
            if (e.key === 'Escape') setProfileOpen(false);
        }
        if (profileOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [profileOpen]);

    // Close org dropdown on click outside / Escape
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target as Node)) {
                setOrgDropdownOpen(false);
            }
        }
        function handleEscape(e: KeyboardEvent) {
            if (e.key === 'Escape') setOrgDropdownOpen(false);
        }
        if (orgDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [orgDropdownOpen]);

    // ── Wizard helpers (declared BEFORE the useEffect that references them) ──
    const openWizard = useCallback((pathId?: string, subPathId?: string, opts?: { resumeDraft?: boolean; operationId?: string; prefill?: WizardPrefill; fixedTemplateId?: string }) => {
        setWizardInitialPath(pathId);
        setWizardPrefill(opts?.prefill);
        setWizardFixedTemplate(opts?.fixedTemplateId);
        setWizardInitialSubPath(subPathId);
        setWizardResumeDraft(!!opts?.resumeDraft);
        setWizardOperationId(opts?.operationId);
        setWizardOpen(true);
        setCommandOpen(false);
    }, []);

    const closeWizard = useCallback(() => {
        setWizardOpen(false);
        setWizardInitialPath(undefined);
        setWizardInitialSubPath(undefined);
        setWizardResumeDraft(false);
        setWizardOperationId(undefined);
        setWizardPrefill(undefined);
        setWizardFixedTemplate(undefined);
        setWizardHandoff(null);
    }, []);

    // flow-3 (FLOW_SCREEN_SPEC.md §2.8): the sheet opens again when the real
    // form it handed the person to has finished. Posted: on its Done card,
    // wherever the person is (the form's page). Closed without posting: at Who,
    // but only on the page the flow started from, where the form's Back lands;
    // so the form's own page never opens it first and then walks away from it.
    const handoffState = useHandoffState();
    const handoffKey = handoffState && handoffState.stage !== 'away' && handoffState.handoff.orgId === currentOrg?.id
        && (handoffState.stage === 'done' || handoffState.handoff.returnTo.split('?')[0] === routerState.location.pathname)
        ? `${handoffState.stage}:${handoffState.handoff.operationId}`
        : null;
    // Adjusted during render (React's "state from a changing input" pattern):
    // each returning handoff opens the sheet once; the sheet then lets it go.
    const [takenHandoffKey, setTakenHandoffKey] = useState<string | null>(null);
    if (handoffKey && handoffState && handoffKey !== takenHandoffKey) {
        setTakenHandoffKey(handoffKey);
        setWizardInitialPath(handoffState.handoff.pathId ?? undefined);
        setWizardInitialSubPath(handoffState.handoff.subPathId);
        setWizardResumeDraft(false);
        setWizardOperationId(undefined);
        setWizardHandoff(handoffState);
        setWizardOpen(true);
    }

    // Cmd+K / Ctrl+K — command palette
    // Cmd+N / Ctrl+N — transaction wizard
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setCommandOpen((prev) => !prev);
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
                e.preventDefault();
                openWizard();
            }
            // Ctrl+\ / ⌘\ — another window, on this page
            if (sideViewAvailable && isSideViewShortcut(e)) {
                e.preventDefault();
                openWindow();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [openWizard, sideViewAvailable, openWindow]);

    // aa-4: let any route (e.g. Bank Feeds "Record") open the wizard pre-selected
    // to a scenario. A window CustomEvent keeps the wizard AppLayout-local while
    // still being reachable from a child route. The human confirms every post.
    useEffect(() => {
        const onOpen = (e: Event) => {
            const d = (e as CustomEvent<{ pathId?: string; subPath?: string; resumeDraft?: boolean; operationId?: string; prefill?: WizardPrefill; fixedTemplateId?: string }>).detail || {};
            // flow-8 (§6.1): a bank line or a document opens the flow with what it already says.
            // fixdue: a fixed expense's reminder opens the record step on that very expense.
            openWizard(d.pathId, d.subPath, { resumeDraft: d.resumeDraft, operationId: d.operationId, prefill: d.prefill, fixedTemplateId: d.fixedTemplateId });
        };
        window.addEventListener('karr:open-wizard', onOpen as EventListener);
        return () => window.removeEventListener('karr:open-wizard', onOpen as EventListener);
    }, [openWizard]);

    // flow-2 (FLOW_SCREEN_SPEC.md §2.4): a one-shot deep link `?record=<id>`
    // opens the question flow at that sub-path (or path), then the address
    // drops it so a reload does not reopen it (the way /sales?open= does).
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const record = params.get('record');
        if (!record) return;
        const url = new URL(window.location.href);
        url.searchParams.delete('record');
        window.history.replaceState(window.history.state, '', url.toString());
        // eslint-disable-next-line react-hooks/set-state-in-effect -- a one-shot read of the address, consumed and stripped
        if (/^[a-z_]+\.[a-z_]+$/.test(record)) openWizard(undefined, record);
        else openWizard(record);
    }, [routerState.location.search, openWizard]);

    // ── Sequence-aware keyboard shortcuts (N→I, G→D, ? for help) ──
    const shortcutDefs = useKeyboardShortcuts({
        onCommandPalette: () => setCommandOpen(prev => !prev),
        onNewTransaction: () => openWizard(),
        onNewInvoice: () => openWizard(undefined, 'sale.invoice'),
        onNewBill: () => openWizard(undefined, 'purchase.bill'),
        onNewExpense: () => openWizard(undefined, 'purchase.expense'),
        onShortcutHelp: () => setShortcutHelpOpen(prev => !prev),
    });

    const handleNavigate = useCallback((href: string) => {
        // vsidebar-a: a row that leaves a vertical (its Books row) leaves the floating way back to
        // the desk that was open: "Back to Stay · Front desk".
        const nav = useVerticalNav.getState().nav;
        if (nav && verticalZonesFor(nav, window.location.pathname) && leavesVertical(nav, href)) {
            const { pathname, search, hash } = window.location;
            setTraceBack({ href: pathname + search + hash, label: verticalBackLabel(nav, pathname, search) });
        }
        // A vertical's row carries its tab as a search (`/stay?tab=rooms`): the router takes it apart.
        const q = href.indexOf('?');
        if (q === -1) { navigate({ to: href }); return; }
        const [qs, hash] = href.slice(q + 1).split('#');
        navigate({ to: href.slice(0, q), search: Object.fromEntries(new URLSearchParams(qs)) as never, ...(hash ? { hash } : {}) });
    }, [navigate]);
    // vsidebar-a: "Books" in the header of every vertical page opens the books dashboard and leaves
    // the floating Back to the desk the person was on.
    const booksButton = !!verticalNav && !!verticalZones && showBooksButton({ prefix: verticalNav.prefix, orgRole, access: vAccess });
    // vsidebar-b: a page this person may not open says so, with who to ask, never a blank page.
    const pageGate = pageAccess(vAccess, currentRoute);

    // whereis-6: a place found in the search box. A wizard answer opens the
    // wizard at it; the palette's own commands run; an office place found inside
    // a client's books switches to the office and goes there in one press.
    const goToPlace = useCallback((place: Place, shut?: ShutReason) => {
        setCommandOpen(false);
        if (place.wizard) {
            if (place.wizard.sub) openWizard(undefined, place.wizard.sub);
            else openWizard(place.wizard.path);
            return;
        }
        if (place.kind === 'action') {
            if (place.id === 'action:wizard-open') openWizard();
            else if (place.id === 'action:new-window') openWindow();
            else if (place.id === 'action:shortcut-help') setShortcutHelpOpen(true);
            return;
        }
        // An agent opens in the Karr Force panel, never on /karr-agents (staff only).
        if (place.kind === 'agent') {
            window.dispatchEvent(new CustomEvent('karr-open-ai-panel', { detail: { agentId: place.id.slice('agent:'.length) } }));
            return;
        }
        if (shut === 'office' && officeOrg && currentOrg?.id !== officeOrg.id) switchOrg(officeOrg.id);
        const [beforeHash, hash] = place.to.split('#');
        const [path, search] = beforeHash.split('?');
        navigate({
            to: path,
            ...(search ? { search: Object.fromEntries(new URLSearchParams(search)) as never } : {}),
            ...(hash ? { hash } : {}),
        });
    }, [openWizard, openWindow, officeOrg, currentOrg?.id, switchOrg, navigate]);

    // whereis-6: who this person is, here, for the places the search box may
    // show: read from what the shell already holds, never a new read.
    const paletteWhere: PaletteWhere = {
        settings: {
            ctx: {
                country: (currentOrg as { country?: string } | null)?.country || 'IN',
                orgType: currentOrg?.org_type,
                activeModules,
                role: orgRole,
                has: hasEntitlement,
                practiceOrg: isPracticeOrg,
                inPractice: officeOrg != null,
                visiting: visitingClientBooks,
                // teamscope: any client's books, as Settings reads it
                inClientBooks: clientName !== null,
            },
            view: experienceLevel === 'business_owner' ? 'flow' : 'books',
            firm: officeOrg?.name ?? null,
        },
        recentKey: user?.id && currentOrg?.id ? `${user.id}:${currentOrg.id}` : null,
        explain: !IN_WINDOW && getModuleKeyForPath(routerState.location.pathname) != null,
        go: goToPlace,
        // whereis-8: "Not here? Ask AI", on this business's key (or its firm's).
        ai: user?.id && currentOrg?.id ? { orgId: currentOrg.id, orgName: currentOrg.name ?? null, memoryKey: `${user.id}:${currentOrg.id}` } : null,
    };

    // A voucher opened from Cmd+K global search (transaction hits open the full
    // voucher in place — P1-7 Voucher Omniscience — instead of dumping the user on
    // the list page the row lives in).
    const [searchVoucherTxn, setSearchVoucherTxn] = useState<string | null>(null);

    // Global data search feeding the Cmd+K palette (contacts/items/accounts/txns/employees).
    const handleGlobalSearch = useCallback(async (q: string): Promise<CommandItem[]> => {
        if (!currentOrg?.id) return [];
        const { data, error } = await supabase.rpc('global_search', { p_org_id: currentOrg.id, p_query: q });
        if (error) return [];
        const rows = (data as { type: string; id: string; label: string; sublabel: string; href: string }[]) ?? [];
        return rows.map((r) => ({
            id: `search-${r.type}-${r.id}`,
            label: r.label,
            description: r.sublabel,
            category: 'result' as const,
            onSelect: r.type === 'transaction'
                ? () => setSearchVoucherTxn(r.id)
                // practice-12: a matter (found by its title, number, case number or CNR) opens on its own page
                : r.type === 'matter'
                    ? () => navigate({ to: r.href, search: { tab: 'matters', matter: r.id } as never })
                    : () => navigate({ to: r.href }),
        }));
    }, [currentOrg, navigate]);



    // A full name only when it is a name: the signup trigger stores the email
    // there until one is typed (flow-1b; the Flow home's greeting uses the same test).
    const rawFullName = user?.user_metadata?.full_name as string | undefined;
    const userDisplayName = (firstNameFor(rawFullName) ? rawFullName : '') || user?.email?.split('@')[0] || 'User';
    const userEmail = user?.email || '';
    const userAvatar = user?.user_metadata?.avatar_url || user?.user_metadata?.picture || '';




    const commandItems: CommandItem[] = [
        // ── Quick Actions (Wizard) ──
        {
            id: 'wizard-open',
            label: 'New Transaction — What happened?',
            category: 'action',
            icon: <Zap size={16} />,
            shortcut: `${MOD_KEY}N`,
            onSelect: () => openWizard(),
        },
        {
            id: 'wizard-invoice',
            label: 'New Invoice',
            category: 'action',
            icon: <Plus size={16} />,
            shortcut: 'N→I',
            onSelect: () => openWizard(undefined, 'sale.invoice'),
        },
        {
            id: 'wizard-bill',
            label: 'New Bill',
            category: 'action',
            icon: <Plus size={16} />,
            shortcut: 'N→B',
            onSelect: () => openWizard(undefined, 'purchase.bill'),
        },
        {
            id: 'wizard-expense',
            label: 'Quick Expense',
            category: 'action',
            icon: <Plus size={16} />,
            shortcut: 'N→E',
            onSelect: () => openWizard(undefined, 'purchase.expense'),
        },
        {
            id: 'wizard-payment-in',
            label: 'Receive Payment',
            category: 'action',
            icon: <Plus size={16} />,
            onSelect: () => openWizard('money_in'),
        },
        {
            id: 'wizard-payment-out',
            label: 'Make Payment',
            category: 'action',
            icon: <Plus size={16} />,
            onSelect: () => openWizard('money_out'),
        },
        {
            id: 'wizard-transfer',
            label: 'Transfer Money',
            category: 'action',
            icon: <Plus size={16} />,
            onSelect: () => openWizard('transfer'),
        },
        // ── Navigation — Core ──
        {
            id: 'nav-dashboard',
            label: 'Dashboard',
            category: 'navigation',
            icon: <LayoutDashboard size={16} />,
            shortcut: 'G→D',
            onSelect: () => handleNavigate('/'),
        },
        {
            id: 'nav-sales',
            label: 'Sales & Invoices',
            category: 'navigation',
            icon: <FileText size={16} />,
            shortcut: 'G→S',
            onSelect: () => handleNavigate('/sales'),
        },
        {
            id: 'nav-purchases',
            label: 'Purchases & Bills',
            category: 'navigation',
            icon: <Receipt size={16} />,
            shortcut: 'G→P',
            onSelect: () => handleNavigate('/purchases'),
        },
        {
            id: 'nav-contacts',
            label: 'Contacts',
            category: 'navigation',
            icon: <Users size={16} />,
            shortcut: 'G→C',
            onSelect: () => handleNavigate('/contacts'),
        },
        {
            id: 'nav-items',
            label: 'Items & Services',
            category: 'navigation',
            icon: <Package size={16} />,
            shortcut: 'G→I',
            onSelect: () => handleNavigate('/items'),
        },
        {
            id: 'nav-accounts',
            label: 'Chart of Accounts',
            category: 'navigation',
            icon: <Landmark size={16} />,
            shortcut: 'G→A',
            onSelect: () => handleNavigate('/accounts'),
        },
        {
            id: 'nav-journals',
            label: 'Journal Entries',
            category: 'navigation',
            icon: <Calculator size={16} />,
            shortcut: 'G→J',
            onSelect: () => handleNavigate('/journals'),
        },
        // ── Navigation — Reports ──
        {
            id: 'nav-reports',
            label: 'Reports',
            category: 'navigation',
            icon: <BarChart3 size={16} />,
            shortcut: 'G→R',
            onSelect: () => handleNavigate('/reports'),
        },
        {
            id: 'nav-gst',
            label: 'GST Filing Cockpit',
            category: 'navigation',
            icon: <Calculator size={16} />,
            onSelect: () => handleNavigate('/gst-filing'),
        },
        {
            id: 'nav-receivables',
            label: 'Receivables & Payables',
            category: 'navigation',
            icon: <CalendarDays size={16} />,
            onSelect: () => handleNavigate('/receivables'),
        },
        {
            id: 'nav-compliance',
            label: 'Compliance Reports',
            category: 'navigation',
            icon: <Shield size={16} />,
            onSelect: () => handleNavigate('/compliance-reports'),
        },
        {
            id: 'nav-ngo',
            label: 'NGO Reports',
            category: 'navigation',
            icon: <Heart size={16} />,
            onSelect: () => handleNavigate('/ngo-reports'),
        },
        // ── Navigation — More ──
        {
            id: 'nav-expenses',
            label: 'Expenses',
            category: 'navigation',
            icon: <ShoppingCart size={16} />,
            onSelect: () => handleNavigate('/expenses'),
        },
        {
            id: 'nav-multi-currency',
            label: 'Multi-Currency',
            category: 'navigation',
            icon: <Globe size={16} />,
            onSelect: () => handleNavigate('/multi-currency'),
        },
        {
            id: 'nav-tasks',
            label: 'Tasks',
            category: 'navigation',
            icon: <ClipboardCheck size={16} />,
            shortcut: 'G→T',
            onSelect: () => handleNavigate('/tasks'),
        },
        {
            id: 'nav-settings',
            label: 'Settings',
            category: 'navigation',
            icon: <Settings size={16} />,
            onSelect: () => handleNavigate('/settings'),
        },
        {
            id: 'nav-marketplace',
            label: 'Marketplace',
            category: 'navigation',
            icon: <Store size={16} />,
            onSelect: () => handleNavigate('/marketplace'),
        },
        // ── General ──
        ...(sideViewAvailable ? [{
            id: 'new-window',
            label: 'New window: this page, floating on top',
            category: 'action' as const,
            icon: <LayoutGrid size={16} />,
            shortcut: `${MOD_KEY}\\`,
            onSelect: () => { setCommandOpen(false); openWindow(); },
        }] : []),
        {
            id: 'shortcut-help',
            label: 'Keyboard Shortcuts',
            category: 'action',
            icon: <Keyboard size={16} />,
            shortcut: '?',
            onSelect: () => setShortcutHelpOpen(true),
        },
    ];



    return (
        <AppLayoutMountedContext.Provider value={true}>
            {/* Exactly one per app: this branch only runs in the OUTERMOST
                AppLayout, so a page that wraps itself in <AppLayout> cannot
                start a second engine. Renders nothing. */}
            {!IN_WINDOW && <NotificationEngine />}
            {/* rundone: the office's month runs carry on from any page, while any tab is open. Renders nothing. */}
            {!IN_WINDOW && <Suspense fallback={null}><MonthRunWorker /></Suspense>}
            {!IN_WINDOW && <CrmSweepWorker />}
            {/* runmin, re-homed by atrest step 7: the office worker at work, seen from any page but the Day Book. Nothing is drawn (or fetched) unless a return is being worked. */}
            {!IN_WINDOW && <MonthRunPillSlot />}
            <div
                className="flex flex-col h-screen supports-[height:100dvh]:h-dvh overflow-hidden bg-[var(--color-bg)]"
                style={{
                    // Respect device safe areas (landscape notch + home indicator).
                    // env() is 0 where there are no insets, so this is a no-op on desktop.
                    paddingLeft: 'env(safe-area-inset-left)',
                    paddingRight: 'env(safe-area-inset-right)',
                    paddingBottom: 'env(safe-area-inset-bottom)',
                }}
            >
                {/* Safe-area top spacer — fills the notch / Dynamic Island / status-bar
                    zone with the header's glass colour so nothing sits under it. */}
                <div
                    aria-hidden
                    className="shrink-0 bg-white/90 dark:bg-stone-900/90 backdrop-blur-xl"
                    style={{ height: 'env(safe-area-inset-top)' }}
                />
                <a
                    href="#main-content"
                    className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[300] focus:px-3 focus:py-2 focus:rounded-lg focus:bg-teal-700 focus:text-white focus:text-sm"
                >
                    Skip to main content
                </a>
                {/* ─── ⓪ Practice Banner — "Viewing: ABC Pvt Ltd" ─── */}
                {!IN_WINDOW && <PracticeBanner />}
                {/* Setup nudging now lives in the Get Started hub + sidebar item
                    (one guide, not two nags) — the amber banner is retired. */}
                {/* ─── ① GLOBAL HEADER — full-width, premium glass style ─── */}
                {/* Thin brand accent line */}
                {!IN_WINDOW && (<>
                <div className="h-[2px] bg-gradient-to-r from-teal-600 to-teal-800 shrink-0" />
                <header className="relative flex items-center h-10 px-3 sm:px-5 bg-white/90 dark:bg-stone-900/90 backdrop-blur-xl border-b border-stone-200/80 dark:border-stone-800/80 z-30 shrink-0">
                    {/* Header stays clean — ambient doodles live in the content
                        area only (they read as a glitch over the glass header). */}
                    {/* Left: Hamburger (mobile) + Org identity + Search */}
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        {/* Mobile hamburger — nothing to open in Flow (wv-7) or
                            on a full-screen page. The office DOES have something
                            to open now: its own books (office-books). */}
                        {sidebarAvailable && (
                            <button
                                onClick={(e) => {
                                    const icon = e.currentTarget.querySelector('.icon-click-target');
                                    if (icon) { icon.classList.remove('icon-anim-pulse'); void (icon as HTMLElement).offsetWidth; icon.classList.add('icon-anim-pulse'); setTimeout(() => icon.classList.remove('icon-anim-pulse'), 550); }
                                    unlockPanel('menu');
                                    setMobileMenuOpen(true);
                                }}
                                className="md:hidden shrink-0 p-2 -ml-1 text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200 transition-colors"
                                aria-label="Open menu"
                            >
                                <span className="icon-click-target"><Menu size={20} /></span>
                            </button>
                        )}

                        {/* Karr brand. The MARK, not the lockup: this header is h-10,
                            and the lockup's KARR letters are only 29% of its height, so
                            even at 32px they land at a 9px cap — smaller than the org
                            name beside them. The disc reads cleanly at 28px.
                            logohome (founder, 9 Oct 2026): pressing it opens the
                            dashboard, like the Home pill. The negative margin keeps
                            the 40px press area from moving the header. Kept to the
                            bare minimum: the shell's first load sits at its 350 KB cap. */}
                        <button onClick={() => navigate({ to: '/dashboard' })} aria-label="Dashboard" className="-m-1.5 p-1.5">
                            <KarrMark size={28} />
                        </button>

                        {/* Divider */}
                        <div className="hidden sm:block shrink-0 w-px h-5 bg-stone-200 dark:bg-stone-700" />

                        {/* Organisation name / switcher */}
                        <div ref={orgDropdownRef} className="hidden sm:flex items-center relative min-w-0">
                            {/* ob-4: this opens with ONE org too. It used to be inert
                                below two, which meant the owner most likely to want a
                                second set of books — the one who has exactly one —
                                was the only person who could never open the menu that
                                now offers it.
                                nameoval (founder, 3 Oct 2026): a hairline oval round
                                it, "which says this is the client name field". */}
                            <button
                                onClick={() => setOrgDropdownOpen(o => !o)}
                                // orgidfast: a hover or a tab onto it starts the chunk if idle time has not yet.
                                onPointerEnter={() => { void loadOrgSwitchList(); }}
                                onFocus={() => { void loadOrgSwitchList(); }}
                                data-tour="org-name"
                                className={`flex items-center gap-1.5 min-w-0 rounded-full border px-2.5 py-0.5 transition-colors text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:border-teal-400 dark:hover:border-teal-600 cursor-pointer ${orgDropdownOpen ? 'border-teal-400 dark:border-teal-600' : 'border-stone-300/80 dark:border-stone-600'}`}
                                title={orgs.length > 1 ? 'Switch organization' : 'Your businesses'}
                            >
                                {/* namefit (founder, 3 Oct 2026): a fixed width per screen, and the
                                    WHOLE name inside it, smaller or on two lines, never
                                    an ellipsis. Indian company names run 25 to 45
                                    letters ("TLF Talentforge (OPC) Private Limited" is
                                    37): at 210px that is one line at 10-11px. It may
                                    still shrink below its width when the header is
                                    squeezed, and FitText re-fits it there. */}
                                <FitText text={orgName || 'My Organization'}
                                    className="min-w-0 w-[112px] md:w-[140px] lg:w-[180px] xl:w-[210px] 2xl:w-[240px] max-h-9" />
                                {/* Whose books are these, and what am I here as?
                                    This said "CLIENT" for any role but owner, which
                                    is every admin, accountant, auditor and viewer a
                                    business employs: a shop's own manager was told
                                    they were standing in a client. Only a visiting
                                    professional gets the amber CLIENT badge now
                                    (office-guest); everyone else who is not the
                                    owner wears the role they actually hold, which
                                    is the more useful fact anyway. */}
                                {visitingClientBooks ? (
                                    <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-500 bg-amber-100/70 dark:bg-amber-900/25 rounded px-1 py-0.5">
                                        Client
                                    </span>
                                ) : currentOrgRole && currentOrgRole !== 'owner' && (
                                    <span className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-stone-800 rounded px-1 py-0.5">
                                        {ROLE_LABELS[currentOrgRole] ?? currentOrgRole}
                                    </span>
                                )}
                                <ChevronDown size={13} className={`shrink-0 transition-transform duration-150 ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Org switcher dropdown — the SAME list the mobile
                                drawer shows (OrgSwitchList), so the two cannot
                                drift into disagreeing about which businesses you
                                have. This owns only the framing. */}
                            {orgDropdownOpen && (
                                <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150 z-50">
                                    {/* copymenu: the copy button used to sit in the
                                        header row, where it spent width on a control
                                        almost nobody presses and pushed the name it
                                        exists to serve further into its own ellipsis.
                                        The panel is 288px and this row may wrap, so
                                        this is also the first place the name is shown
                                        WHOLE — which is exactly what the copy is for.
                                        Costs a click; the click was already being
                                        paid by the width. */}
                                    {orgName && (
                                        <div className="flex items-start gap-2 px-3 py-2 border-b border-stone-200 dark:border-stone-800 bg-stone-50/70 dark:bg-stone-800/30">
                                            <span className="flex-1 text-[12px] leading-snug font-medium text-stone-700 dark:text-stone-300 break-words">
                                                {orgName}
                                                {/* orgidchips: P G C T ride at the end of the
                                                    name, so the row does not grow for them. */}
                                                <Suspense fallback={null}>
                                                    <OrgIdChips
                                                        config={currentOrg?.org_config} gstins={currentOrg?.gstins} orgType={currentOrg?.org_type}
                                                        npo={currentOrg?.npo_numbers} signer={currentOrg?.signer_din}
                                                        onOpenSheet={currentOrg ? () => {
                                                            setOrgDropdownOpen(false);
                                                            void navigate({ to: '/client-sheet', search: { org: currentOrg.id } });
                                                        } : undefined}
                                                    />
                                                </Suspense>
                                            </span>
                                            <CopyOrgNameButton name={orgName} size={13} pad="p-1" className="-mr-1" />
                                        </div>
                                    )}
                                    <Suspense fallback={null}><OrgSwitchList onDone={() => setOrgDropdownOpen(false)} /></Suspense>
                                </div>
                            )}
                        </div>

                        {/* brctx-1 — WHICH branch's figures am I reading. Beside the
                            entity name because that is where "where am I" is
                            already answered, so the header reads
                            "which entity · which branch · back to my office".
                            Hidden entirely for a single-branch business. */}
                        <BranchScopeSwitcher />

                        {/* cc-11: the OFFICE, one glance up — firm altitude sits
                            beside the entity name so the header reads "which entity
                            am I in · step back to my office". It belongs to the
                            ACCOUNT, not the open entity: inside a client's books it
                            stays, so the way back is always one click.
                            wv-2 — it shows ONLY to people who actually have a
                            practice. It used to also fire on `orgs.length > 1`,
                            which handed a firm-altitude pill to every owner of a
                            second business and (via legacy all-on modules) to
                            brand-new orgs.
                            office-boundary — and now on OWNING one, and it takes
                            you to YOUR office rather than to the office route of
                            whichever org happens to be open. */}
                        {officeOrg && (
                            <button
                                onClick={goToOffice}
                                title={currentOrg?.id === officeOrg.id
                                    ? 'Your office — every client and entity, one screen'
                                    : `Back to your office — ${officeOrg.name ?? 'your practice'}`}
                                className={`flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold transition-colors ${practiceMode
                                    ? 'bg-teal-600 text-white shadow-sm'
                                    : 'text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-950/40 hover:bg-teal-100 dark:hover:bg-teal-900/40'}`}
                            >
                                <Briefcase size={13} />
                                <span className="hidden sm:inline">Office</span>
                            </button>
                        )}

                        {/* vsidebar-a: on a vertical's page, one press to the books, with the way back left floating. */}
                        {booksButton && (
                            <button
                                type="button"
                                onClick={() => handleNavigate('/dashboard')}
                                aria-label="Open Books"
                                className="flex shrink-0 items-center gap-1.5 rounded-md border border-stone-200 px-2 py-1 text-[12px] font-semibold text-stone-700 hover:bg-stone-50 dark:border-stone-700 dark:text-stone-200 dark:hover:bg-stone-800"
                            >
                                <BookOpen size={13} className="text-teal-600 dark:text-teal-400" aria-hidden />
                                <span className="hidden sm:inline">Books</span>
                            </button>
                        )}
                    </div>

                    {/* hdrfit — THE CENTRE LANE. The Karr Force desk and the bay
                        below it hang at the header's exact middle on `left-1/2`,
                        and an absolute element reserves no width at all: on a
                        1100-1350px window the right-hand cluster simply slid under
                        it and the three agent avatars came to rest ON TOP OF the
                        search box (founder, 29 Aug 2026, /practice-crm). This empty
                        column is that middle made real. The two clusters either
                        side are equal-width flex halves, so the lane's centre IS
                        `left-1/2` and nothing can be dealt into it any more.
                        It starts at lg, because below that the two clusters need
                        the whole width and the desk answers on its own floating
                        button instead (KarrAIPanel's `mobileFab`). */}
                    <div aria-hidden className="hidden lg:block w-[136px] shrink-0" />

                    {/* The BAY — the header's bottom line swells outward here: the
                        Karr Force's desk. Lives ONLY below the line (no edges inside
                        the header), and reuses the header's exact bg/border classes
                        so any future header re-skin restyles it automatically. */}
                    <div
                        aria-hidden
                        className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-[calc(100%-1px)] w-[136px] h-[15px] rounded-b-[20px] bg-white/90 dark:bg-stone-900/90 backdrop-blur-xl border-b border-x border-stone-200/80 dark:border-stone-800/80 pointer-events-none z-40"
                    />
                    {/* Center: KarrAI indicator — the moat, front and center */}
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-40 hidden lg:block">
                        <Suspense fallback={null}><KarrAIPanel phoneEntry={census.karrForce} atOffice={officeBooksRail} /></Suspense>
                    </div>

                    {/* Right: Search + Status + Actions — all together */}
                    <div className="flex items-center justify-end gap-1.5 sm:gap-2 shrink-0 lg:flex-1 lg:min-w-0 lg:@container/hdr">
                        {/* sidewin (founder, 26 Sep 2026): this page again, in a
                            window floating on top. Left of Search, where he drew
                            it; the four-square tile is the icon he chose. The
                            count says how many windows are open. On a phone it
                            is in the Ctrl+K palette. */}
                        {/* winmin (founder, 26 Sep 2026): "a minimise button that
                            when clicked sits in the top header". Each put-away
                            window is a chip here, numbered in the order the
                            windows were opened, named from xl up; a click brings
                            it back exactly as it was left. */}
                        {sideViewAvailable && sideWindows.map((w, i) => w.minimized && (
                            <button
                                key={w.id}
                                type="button"
                                onClick={() => restoreWindow(w.id)}
                                aria-label={`Bring back window ${i + 1}: ${w.title}`}
                                title={`${w.title}: bring it back`}
                                className={`flex items-center gap-1.5 h-[30px] max-w-[132px] pl-1 pr-1 ${chipNames ? 'xl:pr-2.5' : ''} shrink-0 rounded-lg border border-teal-200 bg-teal-50 text-teal-900 text-[12px] font-medium whitespace-nowrap hover:bg-teal-100 hover:border-teal-300 dark:bg-teal-950/40 dark:border-teal-800 dark:text-teal-100 dark:hover:bg-teal-900/50 animate-in fade-in zoom-in-95 duration-200 transition-colors`}
                            >
                                <span className="grid place-items-center w-[22px] h-[22px] shrink-0 rounded-md bg-teal-600 text-white text-[11px] font-bold tabular-nums" aria-hidden>{i + 1}</span>
                                {chipNames && <span className="hidden xl:inline truncate">{w.title}</span>}
                            </button>
                        ))}
                        {sideViewAvailable && (
                            <button
                                type="button"
                                onClick={() => openWindow()}
                                aria-label={windowCount > 0
                                    ? `New window: this page, floating on top (${windowCount} open)`
                                    : 'New window: this page, floating on top'}
                                title={`New window (${MOD_KEY}\\)`}
                                className="relative hidden sm:grid place-items-center w-[30px] h-[30px] shrink-0 rounded-[9px] bg-gradient-to-br from-teal-500 to-teal-700 shadow-sm shadow-teal-900/20 ring-1 ring-inset ring-white/15 hover:from-teal-400 hover:to-teal-600 active:scale-95 transition"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
                                    <rect x="1" y="1" width="6" height="6" rx="1.6" fill="white" />
                                    <rect x="9" y="1" width="6" height="6" rx="1.6" fill="white" fillOpacity="0.55" />
                                    <rect x="1" y="9" width="6" height="6" rx="1.6" fill="white" fillOpacity="0.55" />
                                    <rect x="9" y="9" width="6" height="6" rx="1.6" fill="white" fillOpacity="0.55" />
                                </svg>
                                {windowCount > 0 && (
                                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-white text-[10px] font-bold text-teal-800 ring-1 ring-teal-600 tabular-nums">
                                        {windowCount}
                                    </span>
                                )}
                            </button>
                        )}
                        {/* Search trigger — glow on the whole bar */}
                        <button
                            onClick={(e) => {
                                const btn = e.currentTarget;
                                btn.classList.remove('btn-click-search'); void btn.offsetWidth; btn.classList.add('btn-click-search');
                                setTimeout(() => btn.classList.remove('btn-click-search'), 650);
                                setCommandOpen(true);
                            }}
                            aria-label="Search Karr"
                            onPointerEnter={prefetchPalette}
                            onTouchStart={prefetchPalette}
                            onFocus={prefetchPalette}
                            className="flex min-h-10 min-w-10 items-center justify-center gap-2 overflow-hidden px-3 py-1.5 sm:min-h-0 sm:min-w-0 text-sm text-stone-400 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg hover:border-stone-300 dark:hover:border-stone-600 hover:bg-stone-100 dark:hover:bg-stone-700 transition-all"
                        >
                            <Search size={14} className="shrink-0" />
                            {/* hdrfit — the full sentence is a ~300px claim on the
                                header and it used to be made from 640px up, which is
                                where the header has the least room to give. The icon
                                alone, then the words once the window is wide enough.
                                helpsupport: the words invite a question, which the
                                box answers (whereis). The shortcut works throughout.
                                bulbglow (8 Oct 2026): the words follow the room the
                                header's right half really has (container /hdr), not
                                the window: a scrollbar or a badge used to push
                                "Search or ask…" out of its box at 1280 px. Measured on
                                the live header: 463 px holds the icon, 574 the words,
                                627 the words and the shortcut; each switch sits ~16 px
                                above. Between, "Search…". If anything else takes the
                                room (window chips), the words clip inside the box. */}
                            <span className="hidden @min-[590px]/hdr:inline min-w-0 truncate text-stone-400">Search or ask…</span>
                            <span className="hidden @min-[536px]/hdr:inline @min-[590px]/hdr:hidden min-w-0 truncate text-stone-400">Search…</span>
                            <kbd className="hidden @min-[644px]/hdr:inline-flex shrink-0 px-1.5 py-0.5 text-[10px] font-mono bg-stone-100 dark:bg-stone-700 text-stone-400 rounded">
                                {MOD_KEY}K
                            </kbd>
                        </button>

                        {/* helpsupport (7 Oct 2026): Help and Karr support, beside
                            the search box. A laptop gets both buttons, each with its
                            own short list; a phone already has three buttons, so one
                            ? opens a sheet with both. Never just "Support": the
                            Helpdesk module is a business's own helpdesk. */}
                        <div ref={helpRef} className="relative hidden lg:flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => setHelpOpen((o) => (o === 'help' ? null : 'help'))}
                                onPointerEnter={prefetchHelp}
                                onFocus={prefetchHelp}
                                aria-label="Help"
                                aria-expanded={helpOpen === 'help'}
                                title="Help"
                                className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${helpOpen === 'help'
                                    ? 'text-teal-700 bg-teal-50 dark:bg-teal-950/30 dark:text-teal-400'
                                    : 'text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800'}`}
                            >
                                <CircleHelp size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setHelpOpen((o) => (o === 'support' ? null : 'support'))}
                                onPointerEnter={prefetchHelp}
                                onFocus={prefetchHelp}
                                aria-label="Karr support"
                                aria-expanded={helpOpen === 'support'}
                                title="Karr support"
                                className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${helpOpen === 'support'
                                    ? 'text-teal-700 bg-teal-50 dark:bg-teal-950/30 dark:text-teal-400'
                                    : 'text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800'}`}
                            >
                                <Headset size={16} />
                            </button>
                            {/* improve (7 Oct 2026): ask Karr for a feature or a change,
                                with screenshots; the answer comes back in the bell.
                                bulbglow (8 Oct, founder: "some animation on icon and
                                little colorful so that user knows this is something"):
                                always amber; it glows and swings until the person
                                first opens it, then stays lit and still. */}
                            <button
                                type="button"
                                onClick={openImprove}
                                onPointerEnter={prefetchImprove}
                                onFocus={prefetchImprove}
                                aria-label="Improve Karr"
                                title="Improve Karr: ask for a feature or a change"
                                className={`relative flex items-center justify-center w-8 h-8 rounded-lg transition-all ${improveNew ? 'improve-new' : ''} ${improveOpen || landedIdea
                                    ? 'text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400'
                                    : 'text-amber-500 hover:text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30'}`}
                            >
                                <span aria-hidden className="improve-glow" />
                                <Lightbulb size={16} className="improve-bulb relative fill-amber-200/80 dark:fill-amber-400/25" />
                            </button>
                            {(helpOpen === 'help' || helpOpen === 'support') && (
                                <Suspense fallback={null}>
                                    <HelpPanel
                                        which={helpOpen}
                                        anchorRef={helpRef}
                                        onClose={closeHelp}
                                        onFind={() => setCommandOpen(true)}
                                        onShortcuts={() => setShortcutHelpOpen(true)}
                                        explain={paletteWhere.explain}
                                        path={routerState.location.pathname}
                                        view={paletteWhere.settings.view === 'flow' ? 'Flow' : 'Books'}
                                    />
                                </Suspense>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => setHelpOpen('sheet')}
                            onTouchStart={prefetchHelp}
                            onFocus={prefetchHelp}
                            aria-label="Help and Karr support"
                            className="lg:hidden flex min-h-10 min-w-10 sm:min-h-0 sm:min-w-0 sm:w-8 sm:h-8 items-center justify-center shrink-0 rounded-lg text-stone-500 dark:text-stone-400 hover:text-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                        >
                            <CircleHelp size={18} />
                        </button>

                        {/* Divider between search and utility icons */}
                        <div className="hidden lg:block w-px h-5 bg-stone-200 dark:bg-stone-700" />
                        {/* Offline indicator */}
                        {(!isOnline || pendingChanges > 0) && (
                            <button
                                onClick={isOnline && pendingChanges > 0 ? syncNow : undefined}
                                aria-label={!isOnline ? 'Offline mode' : isSyncing ? 'Syncing changes' : 'Sync pending changes'}
                                title={
                                    !isOnline
                                        ? `Offline — ${pendingChanges} change${pendingChanges !== 1 ? 's' : ''} pending`
                                        : isSyncing
                                            ? 'Syncing changes...'
                                            : `${pendingChanges} pending — click to sync`
                                }
                                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full transition-all ${!isOnline
                                    ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/40'
                                    : isSyncing
                                        ? 'bg-teal-50 dark:bg-teal-950/20 text-teal-700 dark:text-teal-500 border border-teal-300 dark:border-teal-900/40'
                                        : 'bg-teal-50 dark:bg-teal-950/20 text-teal-700 dark:text-teal-500 border border-teal-300 dark:border-teal-900/40 hover:bg-teal-100 dark:hover:bg-teal-950/30 cursor-pointer'
                                    }`}
                            >
                                {!isOnline ? (
                                    <WifiOff size={12} className="animate-pulse" />
                                ) : isSyncing ? (
                                    <RefreshCw size={12} className="animate-spin" />
                                ) : (
                                    <CloudOff size={12} />
                                )}
                                <span className="tabular-nums hidden xl:inline">
                                    {!isOnline
                                        ? `Offline${pendingChanges > 0 ? ` · ${pendingChanges}` : ''}`
                                        : isSyncing
                                            ? 'Syncing…'
                                            : `${pendingChanges} pending`
                                    }
                                </span>
                            </button>
                        )}

                        {/* UI sound control (founder, 2026-07-25): a small panel with
                            on/off, a volume bar and a choice of sounds for the interface
                            ticks (scroll arrows, task/client clicks). Every choice is
                            remembered across reloads and previews itself when changed. */}
                        <div className="relative hidden lg:block" data-sound-panel>
                            <button
                                onClick={() => setSoundOpen((o) => !o)}
                                onPointerEnter={prefetchSound}
                                onFocus={prefetchSound}
                                className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${soundOpen
                                    ? 'text-teal-700 bg-teal-50 dark:bg-teal-950/30 dark:text-teal-400'
                                    : 'text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800'}`}
                                title="Interface sounds"
                                aria-label="Interface sound settings"
                            >
                                {uiSound && uiVol > 0 ? <Volume2 size={15} /> : <VolumeX size={15} />}
                            </button>
                            {soundOpen && (
                                <Suspense fallback={null}>
                                    <SoundPanel uiSound={uiSound} onUiSound={setUiSound} uiVol={uiVol} onUiVol={setUiVolState} />
                                </Suspense>
                            )}
                        </div>

                        {/* The Karr Phone — messages, alerts, referrals and requests behind
                            ONE icon with ONE number (founder, 2026-07-20). It replaced the
                            separate messages icon and the bell. */}
                        <Suspense fallback={null}>
                            <Karrphone
                                orgId={currentOrg?.id}
                                officeOrgId={phoneOffice?.id ?? null}
                                /* waoffice: any client's books, a client the office
                                   created (and so owns) included, keep the office's
                                   WhatsApp, mail and referrals on the phone. */
                                visitingClient={clientName}
                                notifications={realtimeNotifications}
                                onMarkRead={markNotifRead}
                                onMarkAllRead={markAllNotifsRead}
                                /* The phone hands back plain hrefs, and several of them
                                   carry a fragment (`/settings#email-connect`) that
                                   decides which tab — and now which screen — opens.
                                   `to` alone does not parse one, so the fragment was
                                   being dropped and "Connect my mailbox" landed on the
                                   first Settings tab instead of the mailbox setup. */
                                onNavigate={(href) => {
                                    // gridsees: a link with a query (`/my-work?task=<id>`) is a
                                    // whole href, which the router parses itself; as a `to` the
                                    // query would be read as part of the path.
                                    if (href.includes('?')) { navigate({ href }); return; }
                                    const [to, hash] = href.split('#');
                                    navigate(hash ? { to, hash } : { to });
                                }}
                            />
                        </Suspense>

                        {/* ═══ Admin Console — platform admins only ═══ */}
                        {isPlatformAdmin && (
                            <button
                                id="btn-admin-console"
                                onClick={() => navigate({ to: '/admin' })}
                                className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${currentRoute.startsWith('/admin')
                                    ? 'text-indigo-700 bg-indigo-50 dark:bg-indigo-950/20 dark:text-indigo-400'
                                    : 'text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800'
                                    }`}
                                title="Admin Console"
                            >
                                <Shield size={16} />
                            </button>
                        )}

                        {/* ═══ Flow ⇄ Books — the view switch, in the header
                            where the founder asked for it (wv-5; names wv-6):
                            before the gear, hover explains, click flips. ═══ */}
                        <ViewSwitch />

                        {/* themebtn (founder, 3 Nov 2026): light ⇄ dark in one click,
                            the one exception to the frozen header (PLAIN_FACE_SPEC
                            §9). Laptop only; on a phone it is in the avatar menu. */}
                        <ThemeButton className="hidden lg:flex" />

                        {/* ═══ Settings gear — navigates to full settings page (Zoho pattern) ═══ */}
                        {(() => {
                            const SETTINGS_ROUTES = ['/settings', '/collaboration', '/email', '/ai-settings', '/import', '/api-'];
                            const isSettingsActive = SETTINGS_ROUTES.some(r => currentRoute.startsWith(r));
                            return (
                                <button
                                    id="btn-settings-gear"
                                    onClick={(e) => {
                                        const icon = e.currentTarget.querySelector('.icon-click-target');
                                        if (icon) { icon.classList.remove('icon-anim-spin'); void (icon as HTMLElement).offsetWidth; icon.classList.add('icon-anim-spin'); setTimeout(() => icon.classList.remove('icon-anim-spin'), 400); }
                                        navigate({ to: '/settings' });
                                    }}
                                    className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all ${isSettingsActive
                                        ? 'text-teal-700 bg-teal-50 dark:bg-teal-950/20 dark:text-teal-500'
                                        : 'text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800'
                                        }`}
                                    title="Settings"
                                >
                                    <span className="icon-click-target"><Settings size={16} /></span>
                                </button>
                            );
                        })()}

                        {/* + Record — the "What happened?" wizard, in BOTH views
                            (wv-3). wv-12 (founder): icon-only — the labelled
                            button ate header width; the teal + IS the identity,
                            and Flow's Home already asks the question in words. */}
                        <button
                            id="btn-new-transaction"
                            onClick={(e) => {
                                const icon = e.currentTarget.querySelector('.icon-click-target');
                                if (icon) { icon.classList.remove('icon-anim-rotate'); void (icon as HTMLElement).offsetWidth; icon.classList.add('icon-anim-rotate'); setTimeout(() => icon.classList.remove('icon-anim-rotate'), 550); }
                                openWizard();
                            }}
                            className={`${census.headerPlusClass} items-center justify-center shrink-0 w-8 h-8 text-white bg-gradient-to-br from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 rounded-lg shadow-sm shadow-teal-600/25 hover:shadow-md hover:shadow-teal-600/30 transition-all`}
                            aria-label="Record: what happened?"
                            title="Record: what happened? (Ctrl+N)"
                        >
                            <span className="icon-click-target"><Plus size={16} strokeWidth={2.5} /></span>
                        </button>

                        {/* User profile dropdown */}
                        <div ref={profileRef} className="relative ml-0.5">
                            <button
                                onClick={() => setProfileOpen(p => !p)}
                                className="flex items-center gap-1.5 rounded-full transition-all hover:ring-2 hover:ring-teal-400/50 dark:hover:ring-teal-600/30 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                title={userDisplayName}
                            >
                                {userAvatar ? (
                                    <img
                                        src={userAvatar}
                                        alt={userDisplayName}
                                        className="w-7 h-7 rounded-full object-cover ring-1 ring-stone-200 dark:ring-stone-700"
                                        referrerPolicy="no-referrer"
                                    />
                                ) : (
                                    <div className="w-7 h-7 rounded-full bg-teal-100 dark:bg-teal-950/40 flex items-center justify-center text-xs font-semibold text-teal-800 dark:text-teal-400 ring-1 ring-teal-300 dark:ring-teal-900/40">
                                        {userDisplayName.charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </button>

                            {/* Dropdown */}
                            {profileOpen && (
                                <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-stone-800 rounded-xl shadow-xl border border-stone-200 dark:border-stone-700 overflow-hidden z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                                    {/* User info header */}
                                    <div className="px-4 py-3 border-b border-stone-100 dark:border-stone-700">
                                        <div className="flex items-center gap-3">
                                            {userAvatar ? (
                                                <img src={userAvatar} alt={userDisplayName} className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-950 flex items-center justify-center text-base font-semibold text-teal-800 dark:text-teal-300">
                                                    {userDisplayName.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-stone-900 dark:text-stone-100 truncate">{userDisplayName}</p>
                                                <p className="text-xs text-stone-500 dark:text-stone-400 truncate">{userEmail}</p>
                                            </div>
                                        </div>
                                        {currentOrg && (
                                            <div className="mt-2 px-2 py-1 bg-stone-50 dark:bg-stone-700/50 rounded-md">
                                                <p className="text-xs text-stone-500 dark:text-stone-400">Organisation</p>
                                                <p className="text-sm font-medium text-stone-700 dark:text-stone-200 truncate">{currentOrg.name}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* wv-5: the Simple ⇄ Standard switch left this menu for
                                        the header (ViewSwitch) — one door, where it's visible.
                                        Settings → Preferences still carries the same choice. */}

                                    {/* flow-1b: Flow has no sidebar, so the Get Started hub's one
                                        door there was a sidebar zone nobody could see. It lives
                                        here while the journey is open. */}
                                    {!getStarted.loading && !getStarted.complete && !getStarted.completedPreviously && (
                                        <div className="border-t border-stone-100 dark:border-stone-700 py-1">
                                            <button
                                                onClick={() => { setProfileOpen(false); navigate({ to: '/get-started' }); }}
                                                className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-700/50 transition-colors"
                                            >
                                                <Zap size={16} />
                                                Get started ({getStarted.doneCount} of {getStarted.total} done)
                                            </button>
                                        </div>
                                    )}

                                    {/* themebtn: on a phone the header has no room for the
                                        theme icon, so the choice sits here. */}
                                    <div className="lg:hidden border-t border-stone-100 dark:border-stone-700 px-4 py-3">
                                        <p className="text-xs text-stone-500 dark:text-stone-400 mb-2">Screen</p>
                                        <ThemeChoice />
                                    </div>

                                    {/* pm-17: Your Karr Accounting Score, the person's own (§14.3). */}
                                    <div className="border-t border-stone-100 dark:border-stone-700 py-1">
                                        <button
                                            onClick={() => { setProfileOpen(false); navigate({ to: '/learn' }); }}
                                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-700/50 transition-colors"
                                        >
                                            <GraduationCap size={16} />
                                            Your Karr Accounting Score
                                        </button>
                                    </div>

                                    {/* Password — a personal credential, so it lives on the
                                        account menu rather than in org Settings. The wording
                                        follows whether they already have one. */}
                                    <div className="border-t border-stone-100 dark:border-stone-700 py-1">
                                        <a
                                            href="/auth/set-password"
                                            onClick={() => setProfileOpen(false)}
                                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-700/50 transition-colors"
                                        >
                                            <KeyRound size={16} />
                                            {hasPassword(user) ? 'Change password' : 'Set a password'}
                                        </a>
                                    </div>

                                    {/* Logout */}
                                    <div className="border-t border-stone-100 dark:border-stone-700 py-1">
                                        <button
                                            onClick={() => { setProfileOpen(false); signOut(); }}
                                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            <LogOut size={16} />
                                            Log out
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
                </>)}

                {/* noemailtick + gsturgent: GST portal sessions that died. The office
                    reads a still count that opens the fill rail's Urgent rows; a
                    business's own people a moving line. Nothing when none. */}
                {!IN_WINDOW && <GstDisconnectTicker railHere={!hideSidebar} />}

                {/* ─── Body: Sidebar + Content ───
                    fillrail: a column on a phone, so the fill rail's band sits
                    above the page; a row from md up, where it is a column.
                    sidewin: ALWAYS a row inside a window. A window is 710px on a
                    laptop, under md, and as a column its sidebar sat on top of
                    the page and pushed it out of sight at zero height: a blank
                    window whose sidebar "did nothing" and would not scroll
                    (founder, 26 Sep 2026). The main screen is side by side, so
                    the window is too. */}
                <div className={`flex flex-1 ${IN_WINDOW ? 'flex-row' : 'flex-col md:flex-row'} min-h-0 overflow-hidden`}>
                    {/* Desktop Sidebar (hidden on mobile, on full-screen pages
                        and in Flow — wv-7) */}
                    {/* winnav: in a window the sidebar is not in the row at all;
                        it slides out over the page from a tab (WindowNav, below). */}
                    {!noSidebar && !IN_WINDOW && (
                        <div className="hidden md:block">
                            <Sidebar
                                zones={navZones}
                                activeRoute={activeRoute}
                                onNavigate={handleNavigate}
                                autoCollapse={practiceMode}
                                pinnableItems={pinnableHubItems}
                            />
                        </div>
                    )}

                    {/* office-books: at the office altitude the same nav is a
                        rail — one word, Books, until you open it. */}
                    {officeBooksRail && !IN_WINDOW && (
                        <div className="hidden md:block">
                            <OfficeBooksNav
                                zones={navZones}
                                activeRoute={activeRoute}
                                onNavigate={handleNavigate}
                                pinnableItems={pinnableHubItems}
                                firmName={officeOrg?.name ?? currentOrg?.name}
                            />
                        </div>
                    )}

                    {/* winnav: a window's sidebar, out of the way until asked for */}
                    {IN_WINDOW && sidebarAvailable && (
                        <Suspense fallback={null}>
                            <WindowNav
                                zones={navZones}
                                activeRoute={activeRoute}
                                onNavigate={handleNavigate}
                                pinnableItems={pinnableHubItems}
                            />
                        </Suspense>
                    )}

                    {/* Mobile Sidebar Drawer */}
                    {sidebarAvailable && mobileMenuOpen && (
                        <div className="fixed inset-0 z-40 md:hidden">
                            {/* Backdrop */}
                            <div
                                className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
                                onClick={() => setMobileMenuOpen(false)}
                            />
                            {/* Drawer */}
                            <div className="relative z-50 flex h-full w-60 max-w-[85%] flex-col bg-white dark:bg-stone-900 border-r border-stone-200 dark:border-stone-800 animate-in slide-in-from-left duration-200">
                                {/* mobileorg — WHICH BUSINESS, and which branch's
                                    figures. The header answers both from 640px up
                                    and neither below it, so on a phone this drawer
                                    is where "where am I" gets answered. It carries
                                    the close button too: one row, name and the way
                                    out, rather than a floating X over the nav. */}
                                <Suspense fallback={null}><MobileOrgPanel onClose={() => setMobileMenuOpen(false)} /></Suspense>
                                {/* panellock: its own row, since the org row above holds three controls already. */}
                                <PanelLockButton panel="menu" until={menuLock.until} onLock={m => { menuLock.lock(m); setMobileMenuOpen(false); }} onUnlock={menuLock.unlock} variant="row" />
                                <div className="flex-1 min-h-0">
                                    <Sidebar
                                        zones={navZones}
                                        activeRoute={activeRoute}
                                        onNavigate={handleNavigate}
                                        onMobileClose={() => setMobileMenuOpen(false)}
                                        forceExpanded
                                        pinnableItems={pinnableHubItems}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ─── fillrail: what is still to do, until it is all done ─── */}
                    {!hideSidebar && !IN_WINDOW && (
                        <Suspense fallback={null}>
                            <FillRail
                                key={`fillrail-${currentOrg?.id}`}
                                orgId={currentOrg?.id}
                                orgName={orgName}
                                role={currentOrgRole}
                                isPractice={Boolean((currentOrg as { is_practice?: boolean } | null)?.is_practice)}
                                visiting={isVisitingClientBooks(currentOrg?.id, orgs)}
                                getStarted={getStarted}
                                lawsHere={census.obligationPopup && currentRoute !== '/compliance' && !onboarding.loading && onboarding.complete}
                                installHere={census.installBanner}
                                coachModule={getModuleKeyForPath(routerState.location.pathname)}
                                countryCode={(currentOrg as { country?: string } | null)?.country || 'IN'}
                                onSettings={currentRoute.startsWith('/settings')}
                                fixedHere={!(experienceLevel === 'business_owner' && (currentRoute === '/dashboard' || currentRoute === '/'))}
                            />
                        </Suspense>
                    )}

                    {/* Main Content */}
                    <main id="main-content" className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col relative">
                        {/* Ambient business doodles — absolute layer, cards cover it */}
                        <DoodleBackground />
                        {/* PWA: Offline banner */}
                        <NetworkStatusBanner />

                        {/* Page Content.
                            homepill (27 Aug 2026): the fixed Home and Record
                            pills below float OVER the page, and a page's last
                            rows ("Profit for the period" on Reports, on a
                            phone) sat under them with nothing to scroll them
                            clear. When a pill can show, the page gets enough
                            bottom padding that its last line clears it. */}
                        <div className={hideSidebar
                            ? 'p-0 flex-1 relative z-[1]'
                            // winnav: in a window the figures get the space ("no
                            // area consumed by left right top bottom"): a thin margin.
                            : IN_WINDOW
                                ? 'p-2 sm:p-3 flex-1 relative z-[1]'
                                : `p-4 sm:p-6 flex-1 relative z-[1]${
                                (flowNoSidebar || experienceLevel === 'business_owner') ? ' pb-24 sm:pb-24' : ''}`}>
                            {/* ═══ Universal PageHeader — auto-rendered from route registry ═══ */}
                            {(() => {
                                const meta = getRouteMeta(currentRoute);
                                if (!meta || meta.noHeader) return null;
                                const fullBreadcrumbs = [
                                    { label: 'Home', href: '/dashboard' },
                                    ...meta.breadcrumbs,
                                ];
                                // Simple mode: speak plain language in the header, matching the
                                // sidebar's jargon-free labels (translateTerm, previously unused).
                                const termKey = ROUTE_TERM_KEY[currentRoute];
                                const displayTitle = experienceLevel === 'business_owner' && termKey
                                    ? translateTerm(termKey, experienceLevel)
                                    : meta.title;
                                return (
                                    <PageHeader
                                        title={displayTitle}
                                        subtitle={meta.subtitle}
                                        breadcrumbs={fullBreadcrumbs}
                                        onBreadcrumbClick={(href) => navigate({ to: href })}
                                        dense={meta.dense}
                                        _isLayoutHeader
                                    />
                                );
                            })()}
                            {/* ob-2 — the rest of Simple's old sidebar, inside the
                                screen it belongs to. No-op on every other view. */}
                            {experienceLevel === 'business_owner' && census.hubStrip && (
                                <SimpleHubLinks route={currentRoute} navItemIds={navItemIds} />
                            )}
                            {/* tly-5 - a mirrored org says so on every page, and
                                carries the two things a person does next. It
                                renders nothing at all on every other org, which
                                is all of them until an import finishes as Mirror. */}
                            <Suspense fallback={null}><TallyMirrorBanner currentRoute={currentRoute} /></Suspense>
                            {/* Wrap children in context so per-page <PageHeader> knows to skip chrome */}
                            <LayoutHeaderContext.Provider value={!!getRouteMeta(currentRoute) && !getRouteMeta(currentRoute)?.noHeader}>
                                <BillingGate currentRoute={currentRoute}>
                                    <ModuleGate currentRoute={currentRoute}>
                                        {pageGate.ok ? children : <Suspense fallback={null}><VerticalRefusal need={pageGate.key} access={vAccess} orgName={currentOrg?.name} /></Suspense>}
                                    </ModuleGate>
                                </BillingGate>
                            </LayoutHeaderContext.Provider>
                        </div>
                    </main>

                </div>

                {/* ─── sidewin: the floating windows (portaled over the page) ─── */}
                {sideViewAvailable && windowCount > 0 && (
                    <Suspense fallback={null}>
                        <SideWindows
                            orgId={currentOrg?.id ?? null}
                            orgName={currentOrg?.name ?? null}
                            orgNameOf={orgNameOf}
                            onOpen={openWindow}
                        />
                    </Suspense>
                )}
                {/* …and, inside a window, its end of the wire. Renders nothing. */}
                {IN_WINDOW && (
                    <Suspense fallback={null}>
                        <WindowBridge />
                    </Suspense>
                )}

                {/* ─── Tools Dock (right edge) — hidden on full-screen pages ─── */}
                {/* Tool dock is power-user furniture — hidden in Simple mode
                    (unlabeled icon rails are mystery meat to a non-accountant). */}
                {!hideSidebar && !IN_WINDOW && experienceLevel !== 'business_owner' && <Suspense fallback={null}><ToolsDock /></Suspense>}

                {/* ─── Compliance cockpit light — floating, draggable, self-hides ───
                    Appears the moment there's open non-compliance; hidden on the
                    /compliance page itself (the full ledger is already there). */}
                {!IN_WINDOW && census.complianceOrb && currentRoute !== '/compliance' && onboarding.complete && <Suspense fallback={null}><ComplianceOrb key={currentOrg?.id} orgId={currentOrg?.id} dock={currentRoute === '/practice-crm' || currentRoute === '/portfolio' ? 'left' : undefined} /></Suspense>}

                {/* ─── Karr Coach — teaches the current module. fillrail: it never
                    opens by itself; its first tour is a row in the fill rail,
                    and the header's "Explain this" opens it any time. ─── */}
                {(() => {
                    const k = IN_WINDOW ? null : getModuleKeyForPath(routerState.location.pathname);
                    return k ? <Suspense fallback={null}><ModuleCoach moduleKey={k} autoOpen={false} /></Suspense> : null;
                })()}

                {/* Command Palette (global). crmlist-2: in an office it also finds
                    CLIENTS; mounted only while open, so the roster is read on ask. */}
                {commandOpen && (
                    <Suspense fallback={null}>
                        <PaletteWithClients
                            open
                            onClose={() => setCommandOpen(false)}
                            items={commandItems}
                            onSearch={handleGlobalSearch}
                            where={paletteWhere}
                            orgId={currentOrg?.id}
                            office={Boolean((currentOrg as { is_practice?: boolean } | null)?.is_practice)}
                        />
                    </Suspense>
                )}

                {/* Voucher opened from a Cmd+K transaction hit (drill straight to the voucher). */}
                {searchVoucherTxn && <Suspense fallback={null}><VoucherDrawer transactionId={searchVoucherTxn} onClose={() => setSearchVoucherTxn(null)} /></Suspense>}

                {/* helpsupport: the phone's one sheet, Help on top and Karr support below. */}
                {helpOpen === 'sheet' && (
                    <Suspense fallback={null}>
                        <HelpPanel
                            which="sheet"
                            onClose={closeHelp}
                            onFind={() => setCommandOpen(true)}
                            onShortcuts={() => setShortcutHelpOpen(true)}
                            onImprove={openImprove}
                            explain={paletteWhere.explain}
                            path={routerState.location.pathname}
                            view={paletteWhere.settings.view === 'flow' ? 'Flow' : 'Books'}
                        />
                    </Suspense>
                )}

                {/* improve: Improve Karr, from the header button, the phone's Help sheet, or a bell row. */}
                {(improveOpen || landedIdea) && (
                    <Suspense fallback={null}>
                        <ImproveKarr
                            onClose={closeImprove}
                            path={routerState.location.pathname}
                            view={paletteWhere.settings.view === 'flow' ? 'flow' : 'books'}
                            orgId={currentOrg?.id ?? null}
                            startIdea={landedIdea}
                        />
                    </Suspense>
                )}

                {/* Shortcut Help Overlay */}
                {shortcutHelpOpen && (
                    <Suspense fallback={null}>
                        <ShortcutHelp
                            open={shortcutHelpOpen}
                            onClose={() => setShortcutHelpOpen(false)}
                            shortcuts={shortcutDefs}
                        />
                    </Suspense>
                )}

                {/* wv-9 — Flow has no sidebar, so the way BACK must be
                    unmissable from any deep screen: one fixed Home pill,
                    always in the same corner (the breadcrumb also carries
                    Home, but a corner pill needs no reading). */}
                {!IN_WINDOW && census.homePill && (
                    // nozoom: Karr Force left this pill for its own draggable button.
                    <div
                        className="fixed z-[90] left-5 flex items-center h-11 rounded-full bg-white/95 dark:bg-stone-900/95 backdrop-blur border border-stone-200 dark:border-stone-700 shadow-lg shadow-stone-900/10 overflow-hidden"
                        style={{ bottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}
                    >
                        <button
                            onClick={() => navigate({ to: '/dashboard' })}
                            aria-label="Back to Home"
                            className="h-full flex items-center gap-1.5 px-4 text-sm font-semibold text-stone-700 dark:text-stone-200 hover:text-teal-800 dark:hover:text-teal-300 transition-colors"
                        >
                            <Home size={16} strokeWidth={2.25} /> Home
                        </button>
                    </div>
                )}

                {/* ga-7 — wizard-first Simple: a prominent "Record" FAB on mobile,
                    where the header button's label is hidden. Simple view only. */}
                {!IN_WINDOW && census.recordFab && (
                    <button
                        onClick={() => openWizard()}
                        aria-label="Record something"
                        className="sm:hidden fixed z-[90] right-5 flex items-center gap-1.5 px-4 h-12 rounded-full text-white font-semibold bg-gradient-to-r from-teal-600 to-teal-700 shadow-lg shadow-teal-600/30 active:scale-95 transition"
                        style={{ bottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}
                    >
                        <Plus size={18} strokeWidth={2.5} /> Record
                    </button>
                )}

                {/* Transaction Wizard (global, lazy: its chunk loads right after
                    the shell, so the first tap never waits on it). */}
                <Suspense fallback={null}>
                    <TransactionWizard
                        open={wizardOpen}
                        onClose={closeWizard}
                        initialPathId={wizardInitialPath}
                        initialSubPathId={wizardInitialSubPath}
                        resumeDraft={wizardResumeDraft}
                        operationId={wizardOperationId}
                        handoff={wizardHandoff}
                        prefill={wizardPrefill}
                        fixedTemplateId={wizardFixedTemplate}
                    />
                </Suspense>

                {/* KarrAI is now embedded in the header center */}
            </div>
        </AppLayoutMountedContext.Provider>
    );
}

